import Immutable from 'immutable';
import moment from 'moment';
import { all, call, delay, put, select, spawn, take, takeLatest } from 'redux-saga/effects';

import { getParameterByName, getPlatformName } from '@va/util/helpers';

import {
  apiRequestResponseMsg,
  bannerName,
  GDPR_RELEASE_DATE_UNIX_TS,
  lastDateModalShown,
  modalName,
  notificationTransKeys,
  ONE_DAY,
  ONE_HOUR,
} from '@va/constants';
import {
  checkSsrSettings,
  fetchVisitLimitReachedAtSaga,
  setLimitNotificationShown,
  showLimitNotification,
} from '@va/dashboard/sagas/app';
import { activeWebsiteSlice } from '@va/dashboard/slices';
import { UserRole } from '@va/types/website';
import { appHistory } from '@va/shared/router';
import { notAuthRoutes } from '@va/wix/shared/constants';
import { getEditorUrl, getProducts, handleExpiredRefreshToken } from '@va/wix/shared/helpers';
import * as Actions from 'wix/actions';
import { refreshImpersonateToken, refreshToken } from 'wix/apiClient/admin';

import { apiGateway } from '@va/api-client';
import {
  authenticate,
  AUTHENTICATE_FAILED,
  AUTHENTICATE_SUCCEEDED,
  authenticateSucceeded,
  generateImpersonateTokenSucceeded,
  GET_AGREEMENT_FAILED,
  GET_AGREEMENT_SUCCEEDED,
  GET_ANNOUNCEMENTS_SUCCEEDED,
  getAgreement,
  getAnnouncements,
  getDynamicPages,
  getFeaturePermissions,
  getNotificationSettings,
  getPrivacySettings,
  getSsrSettings,
  loadExternalDashboardScript,
  refreshTokenEnded,
  requestSettings,
  requestWebsite,
  SETTINGS_FAILED,
  SETTINGS_SUCCEEDED,
  UPDATE_SETTINGS_FAILED,
  UPDATE_SETTINGS_SUCCEEDED,
  updateSettings,
  WEBSITE_FAILED,
  WEBSITE_SUCCEEDED,
} from '@va/dashboard/actions/api';
import {
  HANDLE_ANNOUNCEMENT_MODAL,
  handleAnnouncementModal,
  INITIALIZE,
  initializeSucceeded,
  setInstanceId,
  setWebsiteProperty,
} from '@va/dashboard/actions/app';
import { closeModal, openModal, showBanner } from '@va/dashboard/actions/ui';
import { getApiRequest } from '@va/dashboard/selectors/api';
import { getAuthenticationInstance, getInstanceId, getUserWebsiteUrl } from '@va/dashboard/selectors/app';
import { getAuthentication, getWebsite, hasFirstLogin, selectWebsiteMonthlyVisits } from '@va/dashboard/selectors/core';
import { aiApiClient } from '@va/dashboard/shared/ai-assistant';
import dayjs from 'dayjs';
import flagsmith from 'flagsmith';
import { handleAnnouncementModalSaga, handleShortBusinessInfoModal } from './modals';

export function* watchers() {
  yield all([takeLatest(INITIALIZE, initialize), takeLatest(HANDLE_ANNOUNCEMENT_MODAL, handleAnnouncementModalSaga)]);
}

function* refreshTokenPeriodically() {
  while (true) {
    try {
      const impersonateDataStringified = getParameterByName('impersonateData');
      let refreshTokenAction = refreshToken;
      let auth = yield select(getAuthentication);
      const now = moment().unix();

      let credentials = {
        impersonateToken: null,
        //auth token is valid for an hour
        tokenExpiry: now + ONE_HOUR,
        //refresh token is valid for 24 hours
        refreshTokenExpiry: now + ONE_DAY,
      };
      let payload = {
        refreshToken: auth.refreshToken,
      };
      if (impersonateDataStringified) {
        refreshTokenAction = refreshImpersonateToken;
        const impersonateData = JSON.parse(impersonateDataStringified);
        yield put(setInstanceId(impersonateData.instanceId));
        payload.refreshToken = impersonateData.refreshToken;
        payload.impersonate = impersonateData.instanceId;
        credentials.impersonateToken = true;
        yield put(authenticateSucceeded(impersonateData));
      }

      const data = yield call(refreshTokenAction, payload);
      credentials = Object.assign(credentials, {
        token: data.token,
        refreshToken: data.refreshToken,
        instanceId: data.id,
      });

      apiGateway.setAuthToken(data.token);
      aiApiClient.setAuthToken(data.token);

      yield put(authenticateSucceeded(credentials));
      yield put(refreshTokenEnded());

      yield delay((ONE_HOUR / 2) * 1000); //half of auth token expire duration (in ms)
    } catch (e) {
      handleExpiredRefreshToken(appHistory.location.pathname);
    }
  }
}

function* initialize() {
  if (notAuthRoutes.includes(appHistory.location.pathname)) {
    return;
  }

  const instance = yield select(getAuthenticationInstance);

  // The upgrade page can be accessed with or without a valid instance id in the URL
  if (!instance && appHistory.location.pathname === '/upgrade') {
    yield put(initializeSucceeded());
    return;
  }

  const impersonateDataStringified = getParameterByName('impersonateData');
  if (impersonateDataStringified) {
    try {
      const impersonateData = JSON.parse(impersonateDataStringified);

      yield put(generateImpersonateTokenSucceeded(impersonateData));
      yield put(setInstanceId(impersonateData.instanceId));

      flagsmith.identify(impersonateData.instanceId, { platform: getPlatformName() });

      yield spawn(refreshTokenPeriodically);
    } catch (e) {
      appHistory.push('/unsupported-localstorage');
    }
  } else {
    yield put(authenticate(instance));
    const authAction = yield take([AUTHENTICATE_SUCCEEDED, AUTHENTICATE_FAILED]);
    if (authAction.type === AUTHENTICATE_SUCCEEDED) {
      yield spawn(refreshTokenPeriodically);
    }
  }

  yield put(requestWebsite());
  const websiteRequest = yield take([WEBSITE_SUCCEEDED, WEBSITE_FAILED]); // Wait for website request to finish.
  const websiteHasFirstLogin = yield select(hasFirstLogin);

  if (websiteRequest.type !== WEBSITE_FAILED) {
    const website = yield select(getWebsite);
    moment.tz.setDefault(website.timezone);
    dayjs.tz.setDefault(website.timezone);
    window.timezone = website.timezone;
  }

  if (
    websiteRequest.type === WEBSITE_FAILED &&
    websiteRequest?.error?.message === apiRequestResponseMsg.NOT_FOUND_WEBSITE
  ) {
    appHistory.push('/not-found-website');
    return;
  }

  if (websiteRequest.type === WEBSITE_FAILED || !websiteHasFirstLogin) {
    // yield spawn(handleFirstLogin); // If website does not exists or does not have first login field => show register modal
    yield put(initializeSucceeded());
    yield put(getFeaturePermissions());

    if (appHistory.location.pathname === '/upgrade') return;

    appHistory.push('/onboarding');

    return;
  } else {
    yield put(getFeaturePermissions());
    yield put(requestSettings());
    yield put(getPrivacySettings());
    yield put(getAnnouncements());

    const settings = yield take([SETTINGS_SUCCEEDED, SETTINGS_FAILED]);
    if (settings.type === SETTINGS_SUCCEEDED) {
      const siteUrl = yield select(getUserWebsiteUrl);
      if (siteUrl !== 'null' && siteUrl && settings.data.url !== siteUrl) {
        const requestData = Immutable.Map({
          websiteUrl: siteUrl,
        });

        yield put(updateSettings(requestData));
      }
    }

    yield spawn(getPremiumPlan);
    yield spawn(getMetaSiteId);

    const websiteId = yield select(getInstanceId);
    // Wix users are always the owner of their website.
    yield put(activeWebsiteSlice.actions.setExtraInfo({ role: UserRole.OWNER, visitLimitReachedAt: [] }));
    yield spawn(fetchVisitLimitReachedAtSaga, { websiteId });

    yield takeLatest(GET_ANNOUNCEMENTS_SUCCEEDED, handleModals);

    yield spawn(handleLimitNotifications);
    yield put(loadExternalDashboardScript());

    yield put(getSsrSettings());
    yield spawn(checkSsrSettings);
    yield put(getDynamicPages());
  }

  yield put(initializeSucceeded());
}

function* handleNewWidgetUsersFirstLogin() {
  yield put(openModal(modalName.firstLogin));

  const updateWebsite = yield take([UPDATE_SETTINGS_SUCCEEDED, UPDATE_SETTINGS_FAILED]);
  if (updateWebsite.type === UPDATE_SETTINGS_SUCCEEDED) {
    yield put(closeModal(modalName.firstLogin));
    yield put(requestSettings());
    yield put(getNotificationSettings());
    yield put(getAgreement());
  }
}

function* handleModals() {
  const website = yield select(getWebsite);

  if (website.installDate > GDPR_RELEASE_DATE_UNIX_TS && !website.hasFirstLogin) {
    //Show firstLogin modal for new users installed after GDPR release and with no firstLogin (from StatCounter).
    yield spawn(handleNewWidgetUsersFirstLogin);
  }

  yield put(handleAnnouncementModal());
  yield spawn(handleShortBusinessInfoModal);
}

function* handleLimitNotifications() {
  const getAgreement = yield take([GET_AGREEMENT_SUCCEEDED, GET_AGREEMENT_FAILED]);
  const agreementSucceeded = getAgreement.type === GET_AGREEMENT_SUCCEEDED;
  const website = yield select(getWebsite);
  const websiteId = yield select(getInstanceId);
  const numberOfMonthlyVisits = yield select(selectWebsiteMonthlyVisits);
  const featurePermissions = yield select(getApiRequest, 'getFeaturePermissions');
  const limit = featurePermissions && featurePermissions.visits && featurePermissions.visits.limit;
  const markVisitorCurrentDate = moment().format('YYYY-MM');
  const modalShowed =
    website.frontendFlags &&
    website.frontendFlags[modalName.visitsLimitReached + `-${lastDateModalShown}`] === markVisitorCurrentDate;
  if (agreementSucceeded && limit && numberOfMonthlyVisits >= (50 * limit) / 100) {
    if (numberOfMonthlyVisits >= limit) {
      yield put(showBanner(bannerName.LIMIT_REACHED));
      modalShowed
        ? yield spawn(showLimitNotification, 100, 'warning', true, notificationTransKeys.visitsLimitReachedUpgrade)
        : yield put(openModal(modalName.visitsLimitReached));
    } else if (numberOfMonthlyVisits >= (90 * limit) / 100) {
      yield spawn(showLimitNotification, 90, 'warning', true);
      setLimitNotificationShown(90, websiteId);
    } else if (numberOfMonthlyVisits >= (75 * limit) / 100) {
      yield spawn(showLimitNotification, 75, 'warning', true);
      setLimitNotificationShown(75, websiteId);
    } else if (numberOfMonthlyVisits >= (50 * limit) / 100) {
      yield spawn(showLimitNotification, 50, 'warning', true);
      setLimitNotificationShown(50, websiteId);
    }
  }
}

function* getPremiumPlan() {
  try {
    const data = yield call(requestPremiumPlan);

    if (data && data.packages.length > 0) {
      yield put(Actions.wix.App.savePremiumPlanSucceeded(data));
    }
  } catch (error) {
    yield put(Actions.wix.App.savePremiumPlanFailed(error));
  }
}

/** @deprecated */
export function* getMetaSiteId() {
  try {
    const website = yield select(getWebsite);
    const editorUrl = yield call(requestEditorUrl);

    const match = editorUrl.match(/[&?]metaSiteId=([0-9a-zA-Z-]*)/i);
    if (match && website.metaSiteId !== match[1]) {
      yield put(setWebsiteProperty('metaSiteId', match[1]));
      yield put(Actions.wix.Api.sendMetaSiteId(match[1]));
    }
  } catch (error) {
    yield put(Actions.wix.Api.sendMetaSiteIdFailed(error));
  }
}

function requestPremiumPlan() {
  return new Promise((resolve, reject) => {
    getProducts((data) => {
      resolve(data);
    }, reject);
  });
}

function requestEditorUrl() {
  return new Promise((resolve, reject) => {
    getEditorUrl((data) => {
      resolve(data);
    }, reject);
  });
}
